<template>
  <div class="top-header" :data-mobile-header="true">
    <button v-if="onBack" class="top-header__back" @click="onBack" :aria-label="t('common.back')">
      <IconChevronLeft />
    </button>
    <slot name="left">
      <button
        v-if="onProfile"
        class="top-header__avatar"
        @click="onProfile"
        :aria-label="t('settings.title')"
      >
        <span class="top-header__avatar-letter">{{ avatarLetter }}</span>
      </button>
    </slot>
    <div class="top-header__center">
      <div v-if="subtitle" class="top-header__subtitle">{{ subtitle }}</div>
      <div class="top-header__title">{{ title }}</div>
    </div>
    <div class="top-header__right">
      <slot name="right" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import IconChevronLeft from '../icons/IconChevronLeft.vue';

const props = defineProps<{
  title: string;
  subtitle?: string;
  name?: string;
  onBack?: () => void;
  onProfile?: () => void;
}>();

const { t } = useI18n();
const avatarLetter = computed(() => (props.name ?? 'T').trim().charAt(0).toUpperCase() || 'T');
</script>

<style scoped lang="scss">
.top-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px var(--screen-pad-x);
  padding-top: max(14px, var(--app-safe-top));
}

.top-header__back,
.top-header__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--bg-card);
  border: 1px solid var(--hairline);
  color: var(--text);
  flex: 0 0 auto;
  transition: background var(--dur-fast) var(--ease-out);

  &:active { background: var(--bg-card-2); }
}

.top-header__avatar-letter {
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 20px;
  color: var(--gold);
  background: var(--grad-gold);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.top-header__center {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.top-header__subtitle {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--gold);
}

.top-header__title {
  font-size: 19px;
  font-weight: 600;
  color: var(--text);
  letter-spacing: -0.005em;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.top-header__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}
</style>
